import type { Address } from 'viem'
import { BAKERYSWAP_BSC, BISWAP_BSC, PANCAKESWAP_BSC, UNISWAP_BSC, WBNB_BSC } from './config'
import type { DexId } from './types'

/**
 * The eight exchanges ("venues") MultiSwap quotes on BNB Smart Chain: the same eight factories the V4 trading competition accepts
 * (views/TradingCompetitionV3/config.ts DEXES; a unit test fails when the two lists differ). Five sources quote them: CADINUdex and
 * PancakeSwap (V2 + V3 each, unchanged since 2026-09-28), Uniswap (V2 + V3), Biswap and BakerySwap.
 *
 * `router` is the ONE contract a trade on that venue is sent to and the approval spender. `wrappedNative` names the router's getter
 * for the wrapped native token (V2 routers: WETH(), BakerySwap: WBNB(), V3 routers: WETH9()); `factoryGetter` the getter whose answer
 * must equal `factory`. fork/venues.live.test.ts reads both on chain for every venue.
 */
export interface Venue {
  dexId: DexId
  /** "Biswap", "Uniswap V3"… */
  name: string
  version: 'v2' | 'v3'
  factory: Address
  router: Address
  factoryGetter: 'factory' | 'factoryV2'
  wrappedNative: 'WETH' | 'WETH9' | 'WBNB'
  quoter?: Address
  /** LP fee as the router applies it. */
  lpFee: string
}

export const CADINU_SMART_ROUTER_BSC: Address = '0x67E0Ff806c1a76c37b62365714fE17A1261568bc'

export const VENUES: readonly Venue[] = [
  { dexId: 'cadinu', name: 'CADINUdex V3', version: 'v3', factory: '0x2cb62aEBa8eB609020129983A5141DB883abca85', router: CADINU_SMART_ROUTER_BSC, factoryGetter: 'factory', wrappedNative: 'WETH9', lpFee: 'per pool (fee tier)' },
  { dexId: 'cadinu', name: 'CADINUdex V2', version: 'v2', factory: '0x5DaF6fEd0E85218B275DB7D86001621043b561DA', router: CADINU_SMART_ROUTER_BSC, factoryGetter: 'factoryV2', wrappedNative: 'WETH9', lpFee: '0.25 %' },
  { dexId: 'pancakeswap', name: 'PancakeSwap V3', version: 'v3', factory: PANCAKESWAP_BSC.v3Factory, router: PANCAKESWAP_BSC.v3SwapRouter, factoryGetter: 'factory', wrappedNative: 'WETH9', quoter: PANCAKESWAP_BSC.quoterV2, lpFee: 'per pool (fee tier)' },
  { dexId: 'pancakeswap', name: 'PancakeSwap V2', version: 'v2', factory: PANCAKESWAP_BSC.v2Factory, router: PANCAKESWAP_BSC.v2Router02, factoryGetter: 'factory', wrappedNative: 'WETH', lpFee: '0.25 %' },
  { dexId: 'uniswap', name: 'Uniswap V3', version: 'v3', factory: UNISWAP_BSC.v3Factory, router: UNISWAP_BSC.swapRouter02, factoryGetter: 'factory', wrappedNative: 'WETH9', quoter: UNISWAP_BSC.quoterV2, lpFee: 'per pool (fee tier)' },
  { dexId: 'uniswap', name: 'Uniswap V2', version: 'v2', factory: UNISWAP_BSC.v2Factory, router: UNISWAP_BSC.v2Router02, factoryGetter: 'factory', wrappedNative: 'WETH', lpFee: '0.30 %' },
  { dexId: 'biswap', name: 'Biswap', version: 'v2', factory: BISWAP_BSC.factory, router: BISWAP_BSC.router, factoryGetter: 'factory', wrappedNative: 'WETH', lpFee: 'per pair (pair.swapFee(), 0.1 % default)' },
  { dexId: 'bakeryswap', name: 'BakerySwap', version: 'v2', factory: BAKERYSWAP_BSC.factory, router: BAKERYSWAP_BSC.router, factoryGetter: 'factory', wrappedNative: 'WBNB', lpFee: '0.30 %' },
]

export const WRAPPED_NATIVE_BSC = WBNB_BSC

/** Number of venues behind the sources enabled on a chain (the "Checking 8 DEXes" figure). */
export function venueCount(dexIds: readonly DexId[]): number {
  return VENUES.filter((v) => dexIds.includes(v.dexId)).length
}

/** The venue a quote executes on (its router + version). */
export function venueOf(dexId: DexId, version: string): Venue | undefined {
  const own = VENUES.filter((v) => v.dexId === dexId)
  return own.find((v) => v.version === version) ?? (own.length === 1 ? own[0] : undefined)
}
